import { useEffect, useRef, useState } from 'react';
import { Route, Switch, Redirect, useLocation } from 'wouter';
import { useAuth, useUser, useClerk } from '@clerk/react';
import { useAuthStore, type AuthUser } from '@/store/auth-store.js';
import { setAuthToken, setTokenProvider, customInstance } from '@edison/api-client-react';
import { Shell } from '@/components/layout/shell.js';
import { ErrorBoundary } from '@/components/error-boundary.js';
import { TourProvider, TourCard, HelpButton } from '@/components/tour/index.js';
import { LoginPage } from '@/pages/auth/login.js';
import { RegisterPage } from '@/pages/auth/register.js';
import { HomePage } from '@/pages/home/index.js';
import { DashboardPage } from '@/pages/dashboard/dashboard.js';
import { ProjectListPage } from '@/pages/projects/project-list.js';
import { ProjectCreatePage } from '@/pages/projects/project-create.js';
import { ProjectDetailPage } from '@/pages/projects/project-detail.js';
import { ActivityCreatePage } from '@/pages/activities/activity-create.js';
import { ActivityDetailPage } from '@/pages/activities/activity-detail.js';
import { AuditLogPage } from '@/pages/audit/audit-log.js';
import { UserListPage } from '@/pages/users/user-list.js';
import { SignalSourcesPage } from '@/pages/signals/signal-sources.js';
import { TimewritingQueuePage } from '@/pages/timewriting/timewriting-queue.js';
import { AbandonedWorkQueuePage } from '@/pages/abandoned-work/abandoned-work-queue.js';
import { ClaimListPage } from '@/pages/claims/claim-list.js';
import { ClaimCreatePage } from '@/pages/claims/claim-create.js';
import { ClaimDetailPage } from '@/pages/claims/claim-detail.js';
import { ClaimReadinessSummary } from '@/pages/claims/claim-readiness-summary.js';
import { GrantsPage } from '@/pages/grants/index.js';
import { ExpenditurePage } from '@/pages/expenditure/index.js';
import { TimesheetsPage } from '@/pages/timesheets/index.js';
import { ProjectExportPage } from '@/pages/project-export/index.js';
import { GrantConfigPage } from '@/pages/grants/grant-config.js';
import { ProfilePage } from '@/pages/profile/index.js';
import { AboutPage } from '@/pages/about/index.js';
import { IntegrationsPage } from '@/pages/integrations/index.js';
import { ArtefactListPage } from '@/pages/artefacts/artefact-list.js';
import { ArtefactCreatePage } from '@/pages/artefacts/artefact-create.js';
import { ArtefactDetailPage } from '@/pages/artefacts/artefact-detail.js';
import { LinkListPage } from '@/pages/links/link-list.js';
import { PortalLoginPage } from '@/pages/audit-portal/portal-login.js';
import { PortalEligibilityPage } from '@/pages/audit-portal/portal-eligibility.js';
import { PortalShell } from '@/pages/audit-portal/portal-shell.js';
import { PortalEvidencePage } from '@/pages/audit-portal/portal-evidence.js';
import { PortalExpenditurePage } from '@/pages/audit-portal/portal-expenditure.js';
import { PortalFindingsPage } from '@/pages/audit-portal/portal-findings.js';
import { PortalAskPage } from '@/pages/audit-portal/portal-ask.js';
import { ReviewerLoginPage } from '@/pages/reviewer-login/reviewer-login.js';
import { ReviewerLandingPage } from '@/pages/reviewer-login/reviewer-landing.js';
import { ReviewerPortalGate } from '@/pages/reviewer-login/reviewer-portal-gate.js';
import { HelpPage } from '@/pages/help/index.js';
import { SettingsPage } from '@/pages/settings/index.js';
import { DevSupportInboxPage } from '@/pages/dev-support-inbox.js';
import { AuditReadinessPage } from '@/pages/audit-readiness/index.js';
import { EvidencePage } from '@/pages/evidence/index.js';
import { AccessTokensPage } from '@/pages/access-tokens/index.js';
import { DeveloperMyWorkPage } from '@/pages/developer/my-work.js';
import { WeeklyRecapPage } from '@/pages/developer/weekly-recap.js';
import { DeveloperMySetupPage } from '@/pages/developer/my-setup.js';

const SIM_MODE =
  !import.meta.env['VITE_API_BASE_URL'] ||
  import.meta.env['VITE_SIM_MODE'] === 'true';

const DEMO_MODE = import.meta.env['VITE_DEMO_MODE'] === 'true';

type AuthState = 'loading' | 'unauthenticated' | 'needs_registration' | 'not_authorized' | 'authenticated' | 'api_error';

function useAuthFlow() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { user: clerkUser } = useUser();
  const [authState, setAuthState] = useState<AuthState>('loading');

  // Stable ref so we always call the latest getToken without adding it to deps
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Register the token provider so every axios request gets a fresh Clerk token.
  // Done outside useEffect so it's set synchronously on first render.
  useEffect(() => {
    setTokenProvider(() => getTokenRef.current());
    return () => setTokenProvider(() => Promise.resolve(null));
  }, []);

  // Use the user's id (string) as the dependency — the clerkUser object reference
  // changes every render even for the same user, which would cause infinite loops
  const clerkUserId = clerkUser?.id;

  // Capture Clerk user fields we need in the effect as stable primitives
  const clerkEmail = clerkUser?.primaryEmailAddress?.emailAddress ?? '';
  const clerkName = clerkUser?.fullName ?? null;

  useEffect(() => {
    // Access store setters without subscribing — avoids Zustand-triggered re-renders
    const { setUser, setLoading, clearUser } = useAuthStore.getState();

    // In demo mode, don't wait for Clerk — production keys are domain-locked to the prod domain
    if (!DEMO_MODE && !isLoaded) {
      setLoading(true);
      return;
    }

    if (!isSignedIn || !clerkUserId) {
      // Demo mode: auto-authenticate without a real Clerk session
      if (DEMO_MODE && SIM_MODE) {
        import('./lib/sim.js').then(async ({ simSetCurrentClerkId }) => {
          if (!localStorage.getItem('demo:seeded')) {
            const { seedDemoRich } = await import('./lib/demo-seed.js');
            await seedDemoRich({
              userId: '00000000-0000-4000-8000-000000000001',
              tenantId: '00000000-0000-4000-8000-000000000002',
            });
          }
          simSetCurrentClerkId('demo_james');
          try {
            const res = await customInstance<{
              data: { id: string; tenantId: string; email: string; name: string | null; role: string } | null;
              meta?: { needsRegistration?: boolean };
            }>({ url: '/api/auth/me', method: 'GET' });
            if (res.data) {
              setUser({
                id: res.data.id,
                tenantId: res.data.tenantId,
                email: res.data.email,
                name: res.data.name,
                role: res.data.role as AuthUser['role'],
              });
              setAuthState('authenticated');
              // Record login timestamp once per browser session for "new since last login" project detection
              if (!sessionStorage.getItem('edison:login-session-recorded')) {
                localStorage.setItem('edison:last-login-at', new Date().toISOString());
                sessionStorage.setItem('edison:login-session-recorded', '1');
              }
              return;
            }
          } catch {}
          clearUser();
          setAuthToken(null);
          setAuthState('unauthenticated');
        });
        return;
      }
      clearUser();
      setAuthToken(null);
      setAuthState('unauthenticated');
      if (SIM_MODE) {
        import('./lib/sim.js').then(({ simSetCurrentClerkId }) => {
          simSetCurrentClerkId(null);
        });
      }
      return;
    }

    if (SIM_MODE) {
      import('./lib/sim.js').then(({ simSetCurrentClerkId }) => {
        simSetCurrentClerkId(clerkUserId);
      });
    }

    getTokenRef.current()
      .then(async (token) => {
        setAuthToken(token);

        try {
          const res = await customInstance<{
            data: { id: string; tenantId: string; email: string; name: string | null; role: string } | null;
            meta?: { needsRegistration?: boolean; notAuthorized?: boolean };
          }>({ url: '/api/auth/me', method: 'GET' });

          // Backend returns { data: null, meta: { notAuthorized: true } } for a Clerk identity
          // that is authenticated but has no Edison user record and no invitation — never treat
          // this as "needs registration" (that would let anyone create a competing tenant/account).
          if (res.meta?.notAuthorized) {
            clearUser();
            setAuthState('not_authorized');
          } else if (res.meta?.needsRegistration || !res.data) {
            // Backend returns { data: null, meta: { needsRegistration: true } } when no tenant exists.
            // Also handle legacy 401 (Clerk JWT unverified) the same way.
            setUser({
              id: clerkUserId,
              tenantId: '',
              email: clerkEmail,
              name: clerkName,
              role: 'viewer',
            });
            setAuthState('needs_registration');
          } else {
            setUser({
              id: res.data.id,
              tenantId: res.data.tenantId,
              email: res.data.email,
              name: res.data.name,
              role: res.data.role as AuthUser['role'],
            });
            setAuthState('authenticated');
            // Record login timestamp once per browser session for "new since last login" project detection
            if (!sessionStorage.getItem('edison:login-session-recorded')) {
              localStorage.setItem('edison:last-login-at', new Date().toISOString());
              sessionStorage.setItem('edison:login-session-recorded', '1');
            }
          }
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 401) {
            // Clerk JWT rejected by backend — treat as needing registration/re-auth
            setUser({
              id: clerkUserId,
              tenantId: '',
              email: clerkEmail,
              name: clerkName,
              role: 'viewer',
            });
            setAuthState('needs_registration');
          } else {
            // Network error, 5xx, etc. — show error, never redirect to /login while
            // Clerk says the user is signed in (that creates a redirect loop).
            clearUser();
            setAuthToken(null);
            setAuthState('api_error');
          }
        }
      })
      .catch(() => {
        // getToken() itself failed — Clerk session problem
        clearUser();
        setAuthState('unauthenticated');
      });
  }, [isLoaded, isSignedIn, clerkUserId]);

  return authState;
}

function ProtectedRoute({
  children,
  authState,
  userRole,
}: {
  children: React.ReactNode;
  authState: AuthState;
  userRole?: string;
}) {
  // Group B: authenticated external reviewers are provisioned as a distinct role with zero
  // main-app access (enforced server-side by every guard). Never render the staff shell for
  // them — send them to their own claim-scoped landing page instead.
  if (authState === 'authenticated' && userRole === 'external_reviewer') {
    return <Redirect to="/reviewer" />;
  }

  if (authState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading…</div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return <Redirect to="/login" />;
  }

  if (authState === 'api_error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-sm font-medium">Unable to reach the server</p>
          <p className="text-xs text-muted-foreground">Check your connection and try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (authState === 'needs_registration') {
    return (
      <RegisterPage
        onRegistered={() => {
          window.location.reload();
        }}
      />
    );
  }

  if (authState === 'not_authorized') {
    return <NotAuthorizedPage />;
  }

  return <>{children}</>;
}

function NotAuthorizedPage() {
  const { signOut } = useClerk();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">No access to this workspace</h1>
        <p className="text-sm text-muted-foreground">
          Your account isn't a member of this Edison workspace. Contact your administrator to
          request an invitation, or sign in with a different account.
        </p>
        <button
          onClick={() => void signOut()}
          className="mt-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const authState = useAuthFlow();
  const { user } = useAuthStore();
  const [location] = useLocation();

  // Demo mode: auto-seed Canopy Analytics fixture data on first authenticated load
  useEffect(() => {
    if (!DEMO_MODE || authState !== 'authenticated' || !user?.id || !user?.tenantId) return;
    if (localStorage.getItem('demo:seeded') === 'true') return;
    import('./lib/demo-seed.js').then(({ seedDemoRich }) => {
      seedDemoRich({ userId: user.id, tenantId: user.tenantId }).then(() => {
        window.location.reload();
      });
    });
  }, [authState, user?.id, user?.tenantId]);

  return (
    <>
      {/* ERA-034: demo mode shows FABRICATED example data — it must never be mistaken for real R&D
          records (Rule Zero). Previously DEMO_MODE suppressed the sim banner entirely, so seeded
          fake data appeared with no indicator. Always show a non-dismissable DEMO banner instead,
          regardless of Clerk auth state (the portal/demo may not be Clerk-authenticated). */}
      {DEMO_MODE && (
        <div
          role="alert"
          className="bg-red-600 text-white px-4 py-2 text-sm font-semibold text-center"
        >
          DEMO MODE — all figures are fabricated example data, NOT real R&D records
        </div>
      )}

      {/* Simulation mode banner — non-dismissable, shown on all authenticated pages */}
      {SIM_MODE && !DEMO_MODE && authState === 'authenticated' && (
        <div
          role="alert"
          className="bg-amber-500 text-amber-950 px-4 py-2 text-sm font-medium text-center"
        >
          Simulation mode — data stored locally, not sent to AWS
        </div>
      )}

      <Switch>
          <Route path="/login" component={LoginPage} />

          {/* Audit portal — separate auth flow (X-Access-Token), dark theme */}
          <Route path="/portal/login" component={PortalLoginPage} />
          <Route path="/portal/eligibility">
            <PortalShell><PortalEligibilityPage /></PortalShell>
          </Route>
          <Route path="/portal/evidence">
            <PortalShell><PortalEvidencePage /></PortalShell>
          </Route>
          <Route path="/portal/expenditure">
            <PortalShell><PortalExpenditurePage /></PortalShell>
          </Route>
          <Route path="/portal/findings">
            <PortalShell><PortalFindingsPage /></PortalShell>
          </Route>
          <Route path="/portal/ask">
            <PortalShell><PortalAskPage /></PortalShell>
          </Route>

          {/* Reviewer portal — Group B: authenticated external reviewers (Clerk email-code
              sign-in, separate from staff SSO). Never sees the main-app shell/nav. */}
          <Route path="/reviewer-login" component={ReviewerLoginPage} />
          <Route path="/reviewer" component={ReviewerLandingPage} />
          <Route path="/reviewer/portal/eligibility">
            <ReviewerPortalGate><PortalShell><PortalEligibilityPage /></PortalShell></ReviewerPortalGate>
          </Route>
          <Route path="/reviewer/portal/evidence">
            <ReviewerPortalGate><PortalShell><PortalEvidencePage /></PortalShell></ReviewerPortalGate>
          </Route>
          <Route path="/reviewer/portal/expenditure">
            <ReviewerPortalGate><PortalShell><PortalExpenditurePage /></PortalShell></ReviewerPortalGate>
          </Route>
          <Route path="/reviewer/portal/findings">
            <ReviewerPortalGate><PortalShell><PortalFindingsPage /></PortalShell></ReviewerPortalGate>
          </Route>
          <Route path="/reviewer/portal/ask">
            <ReviewerPortalGate><PortalShell><PortalAskPage /></PortalShell></ReviewerPortalGate>
          </Route>

          <Route path="/" nest>
            <ProtectedRoute authState={authState} userRole={user?.role}>
              <TourProvider>
                <Shell>
                <ErrorBoundary variant="page" resetKey={location}>
                <Switch>
                  <Route path="/">
                    {user?.role === 'developer' ? <Redirect to="/developer/my-work" /> : <HomePage />}
                  </Route>
                  <Route path="/dashboard" component={DashboardPage} />

                  {/* Projects + TFA + Evidence (all scoped within project detail) */}
                  <Route path="/projects" component={ProjectListPage} />
                  <Route path="/projects/new" component={ProjectCreatePage} />
                  <Route path="/projects/:id" component={ProjectDetailPage} />

                  {/* Evidence — unified search-first page */}
                  <Route path="/evidence" component={EvidencePage} />

                  {/* Artefacts — keep routes for deep links / back-compat */}
                  <Route path="/artefacts" component={ArtefactListPage} />
                  <Route path="/artefacts/new" component={ArtefactCreatePage} />
                  <Route path="/artefacts/:id" component={ArtefactDetailPage} />

                  {/* Evidence links */}
                  <Route path="/links" component={LinkListPage} />

                  {/* Activities */}
                  <Route path="/projects/:projectId/activities/new" component={ActivityCreatePage} />
                  <Route path="/activities/:id" component={ActivityDetailPage} />

                  {/* Audit log */}
                  <Route path="/audit" component={AuditLogPage} />

                  {/* Users (admin only) */}
                  <Route path="/users" component={UserListPage} />

                  {/* Signal sources */}
                  <Route path="/signals" component={SignalSourcesPage} />

                  {/* Developer workspace */}
                  <Route path="/developer/my-work" component={DeveloperMyWorkPage} />
                  <Route path="/recap" component={WeeklyRecapPage} />
                  <Route path="/developer/setup" component={DeveloperMySetupPage} />

                  {/* Timewriting review */}
                  <Route path="/timewriting" component={TimewritingQueuePage} />

                  {/* Abandoned work */}
                  <Route path="/abandoned-work" component={AbandonedWorkQueuePage} />

                  {/* Claims */}
                  <Route path="/claims" component={ClaimListPage} />
                  <Route path="/claims/new" component={ClaimCreatePage} />
                  <Route path="/claims/:id/summary">
                    {(p) => <ClaimReadinessSummary claimId={p.id} />}
                  </Route>
                  <Route path="/claims/:id" component={ClaimDetailPage} />

                  {/* Non-labour expenditure */}
                  <Route path="/expenditure" component={ExpenditurePage} />
                  <Route path="/timesheets" component={TimesheetsPage} />
                  <Route path="/project-export" component={ProjectExportPage} />

                  {/* Grants discovery & alignment */}
                  <Route path="/grants" component={GrantsPage} />
                  <Route path="/settings/grants" component={GrantConfigPage} />

                  {/* About Edison */}
                  <Route path="/about" component={AboutPage} />

                  {/* Integrations */}
                  <Route path="/integrations" component={IntegrationsPage} />

                  {/* User competency profile */}
                  <Route path="/profile" component={ProfilePage} />

                  {/* Help & Support */}
                  <Route path="/help" component={HelpPage} />

                  {/* Settings (unified) */}
                  <Route path="/settings" component={SettingsPage} />

                  {/* Access tokens — top-level management (create is within claim) */}
                  <Route path="/access-tokens" component={AccessTokensPage} />

                  {/* Dev-only support inbox */}
                  <Route path="/dev-support-inbox" component={DevSupportInboxPage} />

                  {/* Audit Readiness — eligibility evidence portfolio */}
                  <Route path="/audit-readiness" component={AuditReadinessPage} />

                  <Route>
                    <Redirect to="/" />
                  </Route>
                </Switch>
                </ErrorBoundary>
                </Shell>
                {/* Tour UI — staff only, never shown to external reviewers or portal users */}
                <TourCard />
                <HelpButton />
              </TourProvider>
            </ProtectedRoute>
          </Route>
        </Switch>
    </>
  );
}
